import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  approveMember,
  createTeam,
  deleteTeam,
  fetchTeamRosterVersion,
  getMyCurrentTeamMembership,
  getTeamMembers,
  getTeams,
  rejectMember,
  removeMember,
  requestJoinTeam,
  setTeamAutoApprove,
  updateTeam,
} from '../services/teamService';
import { Team, TeamMember } from '../types/domain';
import HoldToConfirmButton from './common/HoldToConfirmButton';
import NumericInput from './common/NumericInput';
import TeamSelectModal from './common/TeamSelectModal';
import { supabase } from '../supabaseClient';
import { usePaidPassGate } from '../contexts/PaidPassGateContext';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import { clearMyTeamMembershipCache } from '../utils/myTeamMembershipCache';
import {
  clearTeamSettingsSnapshot,
  loadTeamSettingsSnapshot,
  saveTeamSettingsSnapshot,
  type TeamSettingsSnapshot,
} from '../utils/teamSettingsSnapshotCache';
import { maskEmailForDisplay } from '../utils/loginEmailMask';

const TEAM_ROLE_LABEL: Record<'owner' | 'admin' | 'teacher' | 'viewer', string> = {
  owner: '관장',
  admin: '관리자',
  teacher: '팀장',
  viewer: '멤버',
};

interface TeamClassSettingsSectionProps {
  workspaceId: string;
  /** 프로필 모달 등에서 열릴 때만 true — 열릴 때 버전 비교 후 필요 시에만 DB 재조회 */
  active?: boolean;
  onDataChange: (options?: { selectGradeId?: string }) => void;
  /** 내 팀 소속/승인 상태가 바뀔 때(상단 팀 배지 등) */
  onMembershipChange?: () => void;
}

const TeamClassSettingsSection: React.FC<TeamClassSettingsSectionProps> = ({
  workspaceId,
  active = true,
  onDataChange,
  onMembershipChange,
}) => {
  const { isFeatureLocked, requireFeature } = usePaidPassGate();
  const isFreePass = isFeatureLocked('team_class');
  const [initialLoading, setInitialLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [teamCreateModalOpen, setTeamCreateModalOpen] = useState(false);
  const [teamEditMode, setTeamEditMode] = useState(false);
  const [teamCreateName, setTeamCreateName] = useState('');
  const [teamCreateCode, setTeamCreateCode] = useState('');
  const [teamCreateMemberLimit, setTeamCreateMemberLimit] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamCode, setTeamCode] = useState('');
  const [teamMemberLimit, setTeamMemberLimit] = useState<number | null>(null);
  const [teamActiveCount, setTeamActiveCount] = useState(1);
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null);
  const [teamCreatedBy, setTeamCreatedBy] = useState<string | null>(null);
  const [myUserId, setMyUserId] = useState<string | null>(null);
  const [myTeamRole, setMyTeamRole] = useState<'owner' | 'admin' | 'teacher' | 'viewer' | null>(null);
  const [myTeamMembershipStatus, setMyTeamMembershipStatus] = useState<'approved' | 'pending' | null>(
    null
  );
  const [teamAutoApprove, setTeamAutoApproveState] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [pendingTeamMembers, setPendingTeamMembers] = useState<TeamMember[]>([]);
  const [pendingRemoveMember, setPendingRemoveMember] = useState<TeamMember | null>(null);
  const [pendingDeleteTeam, setPendingDeleteTeam] = useState<boolean>(false);
  /** Realtime debounce / soft-skip (선언은 여기 한 곳만) */
  const realtimeRefreshTimerRef = useRef<number | null>(null);
  const rosterVersionRef = useRef<string | null>(null);
  const membershipStatusRef = useRef<'approved' | 'pending' | null>(null);
  membershipStatusRef.current = myTeamMembershipStatus;

  const applySnapshot = useCallback((snap: TeamSettingsSnapshot) => {
    setMyUserId(snap.userId);
    setMyTeamMembershipStatus(snap.membershipStatus);
    setMyTeamRole(snap.myRole);
    if (snap.team) {
      setCurrentTeamId(snap.team.id);
      setTeamCreatedBy(snap.team.created_by ?? null);
      setTeamName(snap.team.name);
      setTeamCode(snap.team.code);
      setTeamMemberLimit(snap.team.member_limit ?? null);
      setTeamAutoApproveState(!!snap.team.auto_approve_members);
      setTeamMembers(snap.members);
      setPendingTeamMembers(snap.pendingMembers);
      setTeamActiveCount(snap.members.length);
    } else {
      setCurrentTeamId(null);
      setTeamCreatedBy(null);
      setTeamName('');
      setTeamCode('');
      setTeamMemberLimit(null);
      setTeamAutoApproveState(false);
      setTeamMembers([]);
      setPendingTeamMembers([]);
      setTeamActiveCount(0);
    }
  }, []);

  const clearTeamUi = useCallback(() => {
    setCurrentTeamId(null);
    setTeamCreatedBy(null);
    setTeamName('');
    setTeamCode('');
    setTeamMemberLimit(null);
    setTeamAutoApproveState(false);
    setTeamMembers([]);
    setPendingTeamMembers([]);
    setMyTeamRole(null);
    setMyTeamMembershipStatus(null);
    setTeamActiveCount(0);
  }, []);

  const load = useCallback(
    async (options?: { force?: boolean }) => {
      const force = options?.force === true;
      const authResult = await supabase.auth.getUser();
      const userId = authResult.data.user?.id ?? null;
      if (!userId) {
        clearTeamUi();
        setMyUserId(null);
        return;
      }
      setMyUserId(userId);

      const cachedSnap = loadTeamSettingsSnapshot(userId, workspaceId);
      if (cachedSnap && !force) {
        applySnapshot(cachedSnap);
        recordDevLocalStorageProgramCacheUse('팀설정·LS');
      } else if (!cachedSnap) {
        setInitialLoading(true);
      }

      try {
        const activeMembership = await getMyCurrentTeamMembership({
          forceRefresh: force,
        });
        const membership = activeMembership?.membership ?? null;
        const membershipTeam = activeMembership?.team ?? null;
        const membershipStatus =
          membership?.status === 'approved' || membership?.status === 'pending'
            ? membership.status
            : null;

        if (!membership || !membershipStatus || !membershipTeam) {
          const emptyVersion = 'none';
          rosterVersionRef.current = emptyVersion;
          if (!force && cachedSnap?.version === emptyVersion && !cachedSnap.team) {
            applySnapshot(cachedSnap);
            return;
          }
          clearTeamUi();
          saveTeamSettingsSnapshot({
            userId,
            workspaceId,
            version: emptyVersion,
            team: null,
            membershipStatus: null,
            myRole: null,
            members: [],
            pendingMembers: [],
            savedAt: Date.now(),
          });
          return;
        }

        const version = await fetchTeamRosterVersion(membershipTeam.id);
        if (!force && cachedSnap?.version === version && cachedSnap.team?.id === membershipTeam.id) {
          rosterVersionRef.current = version;
          applySnapshot(cachedSnap);
          recordDevLocalStorageProgramCacheUse('팀설정·버전일치');
          return;
        }

        setInitialLoading(true);
        const [teamRows, memberRows] = await Promise.all([
          getTeams(workspaceId),
          getTeamMembers(membershipTeam.id),
        ]);
        const selectedTeam: Team =
          teamRows.find((t) => t.id === membershipTeam.id) ?? membershipTeam;
        const nextTeamMembers = memberRows.filter((r) => r.status === 'approved');
        const nextPendingMembers = memberRows.filter((r) => r.status === 'pending');
        const me = nextTeamMembers.find((r) => r.user_id === userId);
        const nextTeamRole: 'owner' | 'admin' | 'teacher' | 'viewer' | null =
          (me?.role as 'owner' | 'admin' | 'teacher' | 'viewer' | undefined) ??
          (membership.role as 'owner' | 'admin' | 'teacher' | 'viewer' | undefined) ??
          (selectedTeam.created_by === userId ? 'teacher' : null);

        setMyTeamMembershipStatus(membershipStatus);
        setCurrentTeamId(selectedTeam.id);
        setTeamCreatedBy(selectedTeam.created_by ?? null);
        setTeamName(selectedTeam.name);
        setTeamCode(selectedTeam.code);
        setTeamMemberLimit(selectedTeam.member_limit ?? null);
        setTeamAutoApproveState(!!selectedTeam.auto_approve_members);
        setTeamMembers(nextTeamMembers);
        setPendingTeamMembers(nextPendingMembers);
        setMyTeamRole(nextTeamRole);
        setTeamActiveCount(nextTeamMembers.length);
        rosterVersionRef.current = version;

        saveTeamSettingsSnapshot({
          userId,
          workspaceId,
          version,
          team: selectedTeam,
          membershipStatus,
          myRole: nextTeamRole,
          members: nextTeamMembers,
          pendingMembers: nextPendingMembers,
          savedAt: Date.now(),
        });
      } finally {
        setInitialLoading(false);
      }
    },
    [workspaceId, applySnapshot, clearTeamUi],
  );

  useEffect(() => {
    if (!workspaceId || !active) return;
    void load();
  }, [workspaceId, active, load]);

  /**
   * 프로필 모달이 열려 있을 때만 Realtime 구독.
   * - 내 멤버십 행: 승인/거절/제외 즉시 반영
   * - 현재 팀 roster: 팀장의 승인 대기 블록·인원 수 반영
   * - 팀 메타: 자동승인 등
   * 이벤트는 debounce 후 버전 비교 → 바뀐 경우에만 풀 로드(프로필 포함 RPC).
   */
  useEffect(() => {
    if (!active || !myUserId) return;

    const clearTimer = () => {
      if (realtimeRefreshTimerRef.current != null) {
        window.clearTimeout(realtimeRefreshTimerRef.current);
        realtimeRefreshTimerRef.current = null;
      }
    };

    const runRefresh = async () => {
      clearMyTeamMembershipCache(myUserId);
      clearTeamSettingsSnapshot(myUserId, workspaceId);

      const prevStatus = membershipStatusRef.current;
      let nextStatus: 'approved' | 'pending' | null = null;
      let nextTeamId: string | null = null;
      try {
        const activeMembership = await getMyCurrentTeamMembership({ forceRefresh: true });
        const membership = activeMembership?.membership ?? null;
        nextTeamId = activeMembership?.team?.id ?? null;
        nextStatus =
          membership?.status === 'approved' || membership?.status === 'pending'
            ? membership.status
            : null;

        const teamIdForVersion = nextTeamId ?? currentTeamId;
        if (teamIdForVersion) {
          const version = await fetchTeamRosterVersion(teamIdForVersion);
          if (
            rosterVersionRef.current === version &&
            prevStatus === nextStatus &&
            Boolean(currentTeamId) === Boolean(nextTeamId)
          ) {
            return;
          }
          rosterVersionRef.current = version;
        } else if (!prevStatus && !nextStatus && !currentTeamId) {
          return;
        }
      } catch {
        /* 버전 조회 실패 시에도 UI는 force 로드로 맞춤 */
      }

      await load({ force: true });
      onDataChange();
      if (prevStatus !== nextStatus) {
        onMembershipChange?.();
      }
    };

    const scheduleRefresh = () => {
      clearTimer();
      realtimeRefreshTimerRef.current = window.setTimeout(() => {
        realtimeRefreshTimerRef.current = null;
        void runRefresh();
      }, 350);
    };

    const channelName = `team-settings:${myUserId}:${currentTeamId ?? 'none'}`;
    const channel = supabase.channel(channelName);

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'team_members',
        filter: `user_id=eq.${myUserId}`,
      },
      scheduleRefresh
    );

    if (currentTeamId) {
      channel.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'team_members',
          filter: `team_id=eq.${currentTeamId}`,
        },
        scheduleRefresh
      );
      channel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'teams',
          filter: `id=eq.${currentTeamId}`,
        },
        scheduleRefresh
      );
    }

    channel.subscribe();

    return () => {
      clearTimer();
      void supabase.removeChannel(channel);
    };
  }, [
    active,
    myUserId,
    currentTeamId,
    workspaceId,
    load,
    onDataChange,
    onMembershipChange,
  ]);

  const hasTeam = Boolean(teamName && teamCode);
  const isTeamCreator = Boolean(teamCreatedBy && myUserId && teamCreatedBy === myUserId);
  const isLeader =
    isTeamCreator || myTeamRole === 'owner' || myTeamRole === 'admin' || myTeamRole === 'teacher';
  const hasTeamMembership = myTeamMembershipStatus === 'approved' || myTeamMembershipStatus === 'pending';
  // 소속만 기준으로 생성/가입 가능 여부 판단 (workspace teams 잔여 행과 분리)
  const canCreateOrJoinTeam = !hasTeamMembership && !initialLoading;
  const teamActionDisabledTitle = !canCreateOrJoinTeam
    ? hasTeamMembership
      ? '이미 소속된 팀이 있거나 가입 요청이 진행 중입니다. 팀에서 나온 뒤 이용할 수 있습니다.'
      : undefined
    : isFreePass
      ? '이용권 구매 후 이용할 수 있습니다'
      : undefined;
  const myTeamMember = teamMembers.find((member) => member.user_id === myUserId) ?? null;

  const guardCreateOrJoinTeam = (): boolean => {
    if (!canCreateOrJoinTeam) {
      setMessage('이미 소속된 팀이 있거나 가입 요청이 진행 중입니다. 팀에서 나온 뒤 이용할 수 있습니다.');
      return false;
    }
    if (requireFeature('team_class')) return false;
    return true;
  };

  const handleTeamJoin = async (name: string, code: string) => {
    if (!guardCreateOrJoinTeam()) return;
    try {
      const result = await requestJoinTeam(name, code);
      setMessage(result.message);
      setTeamModalOpen(false);
      await load({ force: true });
      onDataChange();
    } catch (e: any) {
      setMessage(e?.message ?? '팀 가입 요청에 실패했습니다.');
    }
  };

  const handleToggleAutoApprove = async () => {
    if (!currentTeamId) return;
    try {
      await setTeamAutoApprove(currentTeamId, !teamAutoApprove);
      setTeamAutoApproveState((p) => !p);
      setMessage(!teamAutoApprove ? '팀원 자동등록 ON' : '팀원 자동등록 OFF');
    } catch (e: any) {
      setMessage(e?.message ?? '설정 변경에 실패했습니다.');
    }
  };

  const handleApproveMember = async (m: TeamMember) => {
    if (!currentTeamId) return;
    try {
      await approveMember(currentTeamId, m.user_id);
      setMessage(`${m.display_name ?? '사용자'} 님을 승인했습니다.`);
      await load({ force: true });
      onDataChange();
    } catch (e: any) {
      setMessage(e?.message ?? '승인 실패');
    }
  };

  const handleRejectMember = async (m: TeamMember) => {
    if (!currentTeamId) return;
    try {
      await rejectMember(currentTeamId, m.user_id);
      setMessage(`${m.display_name ?? '사용자'} 님 거절`);
      await load({ force: true });
      onDataChange();
    } catch (e: any) {
      setMessage(e?.message ?? '거절 실패');
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!currentTeamId || !pendingRemoveMember) return;
    try {
      const isRemovingSelf = pendingRemoveMember.user_id === myUserId;
      await removeMember(currentTeamId, pendingRemoveMember.user_id);
      setMessage(
        isRemovingSelf
          ? '팀에서 나왔습니다.'
          : `${pendingRemoveMember.display_name ?? '사용자'} 님 제외`
      );
      setPendingRemoveMember(null);
      await load({ force: true });
      onDataChange();
    } catch (e: any) {
      setMessage(e?.message ?? '제외 실패');
    }
  };

  const handleConfirmDeleteTeam = async () => {
    if (!currentTeamId) return;
    try {
      await deleteTeam(currentTeamId);
      setMessage('팀이 해체되었습니다.');
      setPendingDeleteTeam(false);
      await load({ force: true });
      onDataChange();
    } catch (e: any) {
      setMessage(e?.message ?? '팀 해체 실패');
    }
  };

  return (
    <>
      {message && <div className="inline-message">{message}</div>}

      <article className="cp-card cp-team-settings-card">
        <h3>팀 설정</h3>
        {initialLoading ? (
          <p className="team-grid-placeholder">팀 정보를 불러오는 중...</p>
        ) : (
          <>
        <div className="cp-team-buttons-row">
          <button
            type="button"
            className={`primary-row-button cp-compact-btn${isFreePass ? ' is-pass-locked' : ''}`}
            disabled={!isFreePass && !canCreateOrJoinTeam}
            title={teamActionDisabledTitle}
            aria-disabled={isFreePass || undefined}
            onClick={() => {
              if (!guardCreateOrJoinTeam()) return;
              setTeamEditMode(false);
              setTeamCreateName('');
              setTeamCreateCode('');
              setTeamCreateMemberLimit('');
              setTeamCreateModalOpen(true);
            }}
          >
            팀 생성하기
          </button>
          <button
            type="button"
            className={`primary-row-button cp-compact-btn${isFreePass ? ' is-pass-locked' : ''}`}
            disabled={!isFreePass && !canCreateOrJoinTeam}
            title={teamActionDisabledTitle}
            aria-disabled={isFreePass || undefined}
            onClick={() => {
              if (!guardCreateOrJoinTeam()) return;
              setTeamModalOpen(true);
            }}
          >
            팀 선택하기
          </button>
        </div>
        {hasTeam && (
          <div className="cp-team-block-wrapper">
            <div className="cp-team-block-card">
              <div className="cp-team-block-card-main">
                <div className="cp-team-block-card-body">
                  <div className="cp-team-block-card-title">{teamName}</div>
                  <div className="cp-team-block-card-count">
                    {teamActiveCount}명
                    {teamMemberLimit !== null ? ` / ${teamMemberLimit}명` : ''}
                  </div>
                </div>
                <div className="cp-team-block-card-actions">
                  {isLeader && (
                    <button
                      type="button"
                      className="track-icon-button"
                      onClick={() => {
                        setTeamEditMode(true);
                        setTeamCreateName(teamName);
                        setTeamCreateCode(teamCode.slice(0, 8));
                        setTeamCreateMemberLimit(teamMemberLimit !== null ? String(teamMemberLimit) : '');
                        setTeamCreateModalOpen(true);
                      }}
                    >
                      <img src="/edit.svg" alt="팀 수정" className="track-icon" />
                    </button>
                  )}
                  {isLeader ? (
                    <button
                      type="button"
                      className="track-icon-button cp-team-block-exit-icon-btn"
                      onClick={() => setPendingDeleteTeam(true)}
                      title="팀 해체하기"
                    >
                      <img src="/logout.svg" alt="팀 해체하기" className="track-icon" />
                    </button>
                  ) : (
                    myTeamMember && (
                      <button
                        type="button"
                        className="track-icon-button cp-team-block-exit-icon-btn"
                        onClick={() => setPendingRemoveMember(myTeamMember)}
                        title="팀 나오기"
                      >
                        <img src="/logout.svg" alt="팀 나오기" className="track-icon" />
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {hasTeam && (
          <div className="team-members-panel">
            <div className="team-members-header">
              <h4>팀원</h4>
              {isLeader && (
                <div className="cp-auto-approve-in-header">
                  <span className="team-auto-approve-label">팀원 자동승인</span>
                  <button
                    type="button"
                    className={'team-auto-approve-toggle' + (teamAutoApprove ? ' on' : '')}
                    onClick={() => void handleToggleAutoApprove()}
                    aria-pressed={teamAutoApprove}
                  >
                    <span className="team-auto-approve-toggle-slider" data-on={!teamAutoApprove}>
                      OFF
                    </span>
                    <span className="team-auto-approve-toggle-slider" data-on={teamAutoApprove}>
                      ON
                    </span>
                  </button>
                </div>
              )}
            </div>
            <div className="team-member-blocks">
              {teamMembers.length === 0 && pendingTeamMembers.length === 0 ? (
                <p className="team-grid-placeholder">등록된 팀원이 없습니다.</p>
              ) : (
                <>
                  {[...teamMembers]
                    .sort((a, b) => {
                      const nameA = (a.display_name ?? a.user_id) || '';
                      const nameB = (b.display_name ?? b.user_id) || '';
                      const isLeaderA = teamCreatedBy === a.user_id ? 1 : 0;
                      const isLeaderB = teamCreatedBy === b.user_id ? 1 : 0;
                      if (isLeaderB - isLeaderA !== 0) return isLeaderB - isLeaderA;
                      const isSelfA = myUserId === a.user_id ? 1 : 0;
                      const isSelfB = myUserId === b.user_id ? 1 : 0;
                      if (isSelfB - isSelfA !== 0) return isSelfB - isSelfA;
                      return nameA.localeCompare(nameB, 'ko');
                    })
                    .map((member) => (
                      <div key={member.user_id} className="team-member-block">
                        <span
                          className={
                            'team-member-role-badge' +
                            (member.role === 'viewer' ? ' member' : ' leader')
                          }
                        >
                          {TEAM_ROLE_LABEL[member.role]}
                        </span>
                        <span className="team-member-identity">
                          <span className="team-member-name">
                            {member.display_name ?? member.user_id}
                          </span>
                          {member.email ? (
                            <span className="team-member-email">
                              {myUserId === member.user_id
                                ? maskEmailForDisplay(member.email)
                                : member.email}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    ))}
                  {pendingTeamMembers.map((member) => (
                    <div key={member.user_id} className="team-member-block pending">
                      <span className="team-member-identity">
                        <span className="team-member-name">
                          {member.display_name ?? member.user_id}
                        </span>
                        {member.email ? (
                          <span className="team-member-email">
                            {myUserId === member.user_id
                              ? maskEmailForDisplay(member.email)
                              : member.email}
                          </span>
                        ) : null}
                      </span>
                      {isLeader && (
                        <span className="team-member-actions">
                          <button
                            type="button"
                            className="team-member-mini-btn approve"
                            onClick={() => void handleApproveMember(member)}
                          >
                            승인
                          </button>
                          <button
                            type="button"
                            className="team-member-mini-btn reject"
                            onClick={() => void handleRejectMember(member)}
                          >
                            거절
                          </button>
                        </span>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}
          </>
        )}
      </article>

      <TeamSelectModal
        isOpen={teamModalOpen}
        onClose={() => setTeamModalOpen(false)}
        onSelect={(name, code) => handleTeamJoin(name, code)}
      />

      {teamCreateModalOpen && (
        <div
          className="cp-modal-backdrop"
          style={{ zIndex: 2500 }}
          onClick={() => {
            setTeamCreateModalOpen(false);
            setTeamEditMode(false);
          }}
        >
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{teamEditMode ? '팀 수정하기' : '팀 생성하기'}</h3>
            <div className="team-create-section">
              <p className="page-desc">팀 이름 설정하기</p>
              <div className="form-row">
                <input
                  value={teamCreateName}
                  onChange={(e) => setTeamCreateName(e.target.value)}
                  placeholder="팀 이름 입력"
                />
              </div>
              <div className="form-row">
                <button
                  type="button"
                  className="cp-btn-secondary"
                  onClick={() => {
                    if (!teamCreateName.trim()) return;
                    alert('사용 가능한 팀 이름입니다.');
                  }}
                >
                  중복확인
                </button>
              </div>
            </div>
            <div className="team-create-section">
              <p className="page-desc">팀 코드 설정하기</p>
              <div className="team-code-grid">
                <input
                  className="team-code-cell"
                  maxLength={8}
                  value={teamCreateCode}
                  onChange={(e) => {
                    setTeamCreateCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 8));
                  }}
                  placeholder="8자리 숫자"
                />
              </div>
            </div>
            <div className="team-create-section">
              <p className="page-desc">팀 구성원 수 설정하기</p>
              <div className="form-row">
                <NumericInput
                  min={1}
                  maxLength={5}
                  value={teamCreateMemberLimit}
                  onChange={(v) =>
                    setTeamCreateMemberLimit(
                      v === '' ? v : String(Math.max(1, parseInt(v, 10) || 0))
                    )
                  }
                  defaultEmptyValue="1"
                  placeholder="예: 30"
                />
              </div>
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => {
                  setTeamCreateModalOpen(false);
                  setTeamEditMode(false);
                  setTeamCreateName('');
                  setTeamCreateCode('');
                  setTeamCreateMemberLimit('');
                }}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={async () => {
                  const code = teamCreateCode;
                  const limit = parseInt(teamCreateMemberLimit || '0', 10);
                  if (!teamCreateName.trim() || code.length !== 8 || !limit || limit <= 0) return;
                  try {
                    if (teamEditMode && currentTeamId) {
                      await updateTeam(currentTeamId, {
                        name: teamCreateName.trim(),
                        code,
                        memberLimit: limit,
                      });
                      setTeamName(teamCreateName.trim());
                      setTeamCode(code);
                      setTeamMemberLimit(limit);
                    } else {
                      if (!guardCreateOrJoinTeam()) return;
                      const created = await createTeam(workspaceId, {
                        name: teamCreateName.trim(),
                        code,
                        memberLimit: limit,
                      });
                      setCurrentTeamId(created.id);
                      setTeamName(created.name);
                      setTeamCode(created.code);
                      setTeamMemberLimit(created.member_limit ?? null);
                      setTeamAutoApproveState(!!created.auto_approve_members);
                    }
                    setTeamCreateModalOpen(false);
                    setTeamEditMode(false);
                    setTeamCreateName('');
                    setTeamCreateCode('');
                    setTeamCreateMemberLimit('');
                    await load({ force: true });
                    onDataChange();
                  } catch (e: any) {
                    setMessage(e?.message ?? '팀 저장 실패');
                  }
                }}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteTeam && (
        <div
          className="cp-modal-backdrop"
          style={{ zIndex: 2500 }}
          onClick={() => setPendingDeleteTeam(false)}
        >
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>팀 해체</h3>
            <p className="page-desc">
              &quot;{teamName}&quot; 팀을 해체할까요? 팀원 정보와 설정이 모두 삭제됩니다.
            </p>
            <p className="page-desc cp-team-hold-confirm-hint">버튼을 길게 누르면 실행됩니다.</p>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setPendingDeleteTeam(false)}
              >
                취소
              </button>
              <HoldToConfirmButton
                actionKey={`team-dissolve-${currentTeamId ?? ''}`}
                variant="danger"
                ariaLabel="팀 해체 (길게 누르기)"
                title="길게 눌러 팀 해체"
                onComplete={() => void handleConfirmDeleteTeam()}
              >
                해체하기
              </HoldToConfirmButton>
            </div>
          </div>
        </div>
      )}

      {pendingRemoveMember && (
        <div
          className="cp-modal-backdrop"
          style={{ zIndex: 2500 }}
          onClick={() => setPendingRemoveMember(null)}
        >
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{pendingRemoveMember.user_id === myUserId ? '팀 나오기' : '팀원 제외'}</h3>
            <p className="page-desc">
              {pendingRemoveMember.user_id === myUserId
                ? `"${teamName}" 팀에서 나올까요?`
                : `"${pendingRemoveMember.display_name ?? pendingRemoveMember.user_id}" 님을 팀에서 제외할까요?`}
            </p>
            {pendingRemoveMember.user_id === myUserId ? (
              <p className="page-desc cp-team-hold-confirm-hint">버튼을 길게 누르면 실행됩니다.</p>
            ) : null}
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setPendingRemoveMember(null)}
              >
                취소
              </button>
              {pendingRemoveMember.user_id === myUserId ? (
                <HoldToConfirmButton
                  actionKey={`team-leave-${pendingRemoveMember.user_id}`}
                  variant="primary"
                  ariaLabel="팀 나가기 (길게 누르기)"
                  title="길게 눌러 팀에서 나가기"
                  onComplete={() => void handleConfirmRemoveMember()}
                >
                  나가기
                </HoldToConfirmButton>
              ) : (
                <button type="button" className="cp-btn-primary" onClick={() => void handleConfirmRemoveMember()}>
                  제외
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeamClassSettingsSection;
